import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { API_ROUTES } from "../configuration/RoutesApi"
import { PublicContactSettingsEntity } from "../Objects/Landing/PublicContactSettingsEntity"
import { GetRequest } from "../services/ApiClientService"

interface PublicContactContextData {
  contact: PublicContactSettingsEntity
  isLoading: boolean
  loadContact: () => void
}

const PublicContactContext = createContext<PublicContactContextData | undefined>(undefined)

export const PublicContactProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [contact, setContact] = useState(() => new PublicContactSettingsEntity())

  const [isLoading, setIsLoading] = useState(false)

  const hasLoadedRef = useRef(false)

  const isLoadingRef = useRef(false)

  const loadContact = useCallback(() => {
    if (hasLoadedRef.current || isLoadingRef.current) {
      return
    }

    isLoadingRef.current = true
    setIsLoading(true)

    GetRequest<PublicContactSettingsEntity>(API_ROUTES.LANDING.GET_CONTACT)
      .then((response) => {
        if (response.success && response.data) {
          setContact(new PublicContactSettingsEntity(response.data))
        }
      })
      .catch(() => {})
      .finally(() => {
        hasLoadedRef.current = true
        isLoadingRef.current = false
        setIsLoading(false)
      })
  }, [])

  return (
    <PublicContactContext.Provider value={{ contact, isLoading, loadContact }}>
      {children}
    </PublicContactContext.Provider>
  )
}

export const usePublicContact = () => {
  const context = useContext(PublicContactContext)

  const loadContact = context?.loadContact

  useEffect(() => {
    loadContact?.()
  }, [loadContact])

  if (!context) {
    throw new Error("usePublicContact must be used inside PublicContactProvider")
  }

  return context
}
